/** 
   Service providing functions for working with data for a particular (single) supplier.
*/
import {
    getFirebaseDefaultUsers,
    getPortalUsersStatic,
    getRawSupplierStats,
    getSupplierDeliveryTypes,
    getSupplierGroups,
    getSupplierInfoFirebase,
} from 'lib/firebase';
import {
    SupplierDeliveryType,
    RawDeliveryType,
    RawSupplierGroups,
    SupplierGroup,
    SupplierInfo,
    RawSupplierStats,
    DefaultUser,
} from 'types';

/** Get array of delivery types associated with the supplier's account. */
export const getDeliveryTypes = (
    supplierId: string
): Promise<SupplierDeliveryType[]> => {
    const SupplierDeliveryTypes: SupplierDeliveryType[] = [];
    return getSupplierDeliveryTypes(supplierId).then(
        (data: RawDeliveryType) => {
            for (const name in data) {
                const willCallValue = data[name];
                const deliveryType: SupplierDeliveryType = {
                    deliveryTypeName: name,
                    willCall: willCallValue,
                };
                SupplierDeliveryTypes.push(deliveryType);
            }

            return SupplierDeliveryTypes;
        }
    );
};

/** Get all groups associated with the supplier's account. */
export const getGroups = (supplierId: string): Promise<SupplierGroup[]> => {
    const supplierGroups: SupplierGroup[] = [];
    return getSupplierGroups(supplierId).then((data: RawSupplierGroups) => {
        for (const groupName in data) {
            const description = data[groupName];
            const group: SupplierGroup = {
                name: groupName,
                description,
            };
            supplierGroups.push(group);
        }
        return supplierGroups;
    });
};

/** Get info associated with supplier. */
export const getSupplierInfo = (supplierId: string): Promise<SupplierInfo> => {
    return getSupplierInfoFirebase(supplierId);
};

/** Get stats for the given supplier. */
export const getSupplierStats = async (
    supplierId: string
): Promise<RawSupplierStats> => {
    return await getRawSupplierStats(supplierId);
};

/** Get list of all default users for the supplier. Gets current default users and initializes portal users who are not yet saved in the default users db table. */
export const getDefaultUsers = (supplierId: string): Promise<DefaultUser[]> => {
    const defaultUsers: DefaultUser[] = [];
    return getFirebaseDefaultUsers(supplierId).then((existingDefaultUsers) => {
        return getPortalUsersStatic(supplierId).then((portalUsers) => {
            for (const i in portalUsers) {
                const uid = portalUsers[i].uid;
                const email = portalUsers[i].email;
                const u: DefaultUser = {
                    uid,
                    email,
                    threshold: false,
                    offline: false,
                    deploy: false,
                    issue: false,
                };

                if (existingDefaultUsers && existingDefaultUsers[uid]) {
                    u.threshold = existingDefaultUsers[uid].threshold;
                    u.offline = existingDefaultUsers[uid].offline;
                    u.deploy = existingDefaultUsers[uid].deploy;
                    u.issue = existingDefaultUsers[uid].issue;
                }

                defaultUsers.push(u);
            }
            return defaultUsers;
        });
    });
};
